// @ts-nocheck
/**
 * Portfolio Items Page
 * Portfolio item upload and management interface with Carbon Card
 * Epic 75 - Work Item 3: R2 Object Storage and Content Delivery Architecture
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Button,
  Tag,
  Breadcrumb,
  BreadcrumbItem,
  Modal,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  Toggle,
  InlineNotification,
  SkeletonPlaceholder,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Tile,
  ClickableTile,
} from '@carbon/react';
import { Add, Portfolio, Edit, Delete, Share, View, Star, StarFilled, CloudUpload } from '@carbon/react/icons';
import { FileUploader } from '../components/storage/FileUploader';
import { useAuth } from '../hooks/useAuth';

interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  category: string;
  fileId: string;
  fileUrl: string;
  thumbnailUrl?: string;
  tags: string[];
  visibility: 'public' | 'private' | 'unlisted';
  featured: boolean;
  viewCount: number;
  likeCount: number;
  createdAt: string;
  updatedAt: string;
}

const PortfolioItems: React.FC = () => {
  const { user } = useAuth();
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PortfolioItem | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('project');
  const [tags, setTags] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private' | 'unlisted'>('public');
  const [featured, setFeatured] = useState(false);
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);

  const fetchPortfolioItems = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
      const response = await fetch('/api/v1/portfolio/items', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch portfolio items');
      }

      const data = await response.json();
      setPortfolioItems(data.items);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPortfolioItems();
  }, []);

  const handleFileUpload = (fileData: any) => {
    setUploadedFileId(fileData.fileId);
  };

  const handleCreateItem = async () => {
    if (!title || !uploadedFileId) {
      alert('Please provide a title and upload a file');
      return;
    }

    try {
      const token = sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
      const response = await fetch('/api/v1/portfolio/items', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title,
          description,
          category,
          fileId: uploadedFileId,
          tags: tags.split(',').map(t => t.trim()),
          visibility,
          featured,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create portfolio item');
      }

      setCreateModalOpen(false);
      resetForm();
      fetchPortfolioItems();
    } catch (error) {
      console.error('Create error:', error);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!confirm('Are you sure you want to delete this portfolio item?')) {
      return;
    }

    try {
      const token = sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
      const response = await fetch(`/api/v1/portfolio/items/${itemId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete portfolio item');
      }

      fetchPortfolioItems();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleShareItem = async (item: PortfolioItem) => {
    const shareUrl = `${window.location.origin}/public/portfolios/${user?.id}#${item.id}`;
    await navigator.clipboard.writeText(shareUrl);
    alert('Share link copied to clipboard!');
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setCategory('project');
    setTags('');
    setVisibility('public');
    setFeatured(false);
    setUploadedFileId(null);
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      project: 'blue',
      certification: 'green',
      achievement: 'purple',
      article: 'teal',
      presentation: 'cyan',
    };
    return colors[category] || 'gray';
  };

  const getVisibilityIcon = (visibility: string) => {
    switch (visibility) {
      case 'public':
        return '🌍';
      case 'private':
        return '🔒';
      case 'unlisted':
        return '🔗';
      default:
        return '';
    }
  };

  const filteredItems = (filter: string) => {
    switch (filter) {
      case 'public':
        return portfolioItems.filter(item => item.visibility === 'public');
      case 'featured':
        return portfolioItems.filter(item => item.featured);
      case 'recent':
        return [...portfolioItems]
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, 6);
      default:
        return portfolioItems;
    }
  };

  if (loading) {
    return (
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <SkeletonPlaceholder key={i} style={{ height: '300px', marginBottom: '16px' }} />
            ))}
          </div>
        </Column>
      </Grid>
    );
  }

  if (error) {
    return <InlineNotification kind="error" title="Error loading portfolio" subtitle={error} />;
  }

  return (
    <div className="portfolio-items-page">
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          {/* Breadcrumb */}
          <Breadcrumb className="mb-4">
            <BreadcrumbItem href="/">Home</BreadcrumbItem>
            <BreadcrumbItem href="/dashboard">Dashboard</BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>Portfolio</BreadcrumbItem>
          </Breadcrumb>

          {/* Page Header */}
          <div className="page-header mb-6 flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-semibold mb-2">My Portfolio</h1>
              <p className="text-gray-600">Showcase your projects, achievements, and certifications</p>
            </div>
            <Button kind="primary" renderIcon={Add} onClick={() => setCreateModalOpen(true)}>
              Add Item
            </Button>
          </div>

          {/* Portfolio Tabs */}
          <Tabs selectedIndex={activeTab} onChange={(e: any) => setActiveTab(e.selectedIndex)}>
            <TabList aria-label="Portfolio views">
              <Tab>All Items ({portfolioItems.length})</Tab>
              <Tab>Public ({filteredItems('public').length})</Tab>
              <Tab>Featured ({filteredItems('featured').length})</Tab>
              <Tab>Recent</Tab>
            </TabList>
            <TabPanels>
              {['all', 'public', 'featured', 'recent'].map((filter, index) => (
                <TabPanel key={filter}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                    {filteredItems(filter).map(item => (
                      <ClickableTile key={item.id} className="portfolio-card" onClick={() => {}}>
                        {/* Tile Header with Image */}
                        <div className="relative">
                          {item.thumbnailUrl ? (
                            <img
                              src={item.thumbnailUrl}
                              alt={item.title}
                              className="w-full h-48 object-cover rounded-t"
                            />
                          ) : (
                            <div className="w-full h-48 bg-gray-100 flex items-center justify-center rounded-t">
                              <Portfolio size={48} />
                            </div>
                          )}
                          {item.featured && (
                            <div className="absolute top-2 right-2">
                              <StarFilled className="text-yellow-500" size={24} />
                            </div>
                          )}
                          <div className="absolute top-2 left-2">
                            <span className="text-white bg-black bg-opacity-50 px-2 py-1 rounded text-xs">
                              {getVisibilityIcon(item.visibility)} {item.visibility}
                            </span>
                          </div>
                        </div>

                        {/* Card Content */}
                        <div className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="text-lg font-semibold">{item.title}</h3>
                            <Tag kind={getCategoryColor(item.category) as any} size="sm">
                              {item.category}
                            </Tag>
                          </div>

                          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                            {item.description || 'No description provided'}
                          </p>

                          {/* Tags */}
                          {item.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {item.tags.slice(0, 3).map(tag => (
                                <Tag key={tag} kind="gray" size="sm">
                                  {tag}
                                </Tag>
                              ))}
                              {item.tags.length > 3 && (
                                <Tag kind="gray" size="sm">
                                  +{item.tags.length - 3} more
                                </Tag>
                              )}
                            </div>
                          )}

                          {/* Stats */}
                          <div className="flex justify-between items-center text-sm text-gray-500 mb-3">
                            <span>{item.viewCount} views</span>
                            <span>{item.likeCount} likes</span>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2">
                            <Button kind="ghost" size="sm" renderIcon={View} href={item.fileUrl} target="_blank">
                              View
                            </Button>
                            <Button kind="ghost" size="sm" renderIcon={Share} onClick={() => handleShareItem(item)}>
                              Share
                            </Button>
                            <Button
                              kind="ghost"
                              size="sm"
                              renderIcon={Edit}
                              onClick={() => {
                                setEditingItem(item);
                                setTitle(item.title);
                                setDescription(item.description);
                                setCategory(item.category);
                                setTags(item.tags.join(', '));
                                setVisibility(item.visibility);
                                setFeatured(item.featured);
                                setCreateModalOpen(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              kind="danger--ghost"
                              size="sm"
                              renderIcon={Delete}
                              onClick={() => handleDeleteItem(item.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </ClickableTile>
                    ))}
                  </div>

                  {filteredItems(filter).length === 0 && (
                    <div className="text-center py-12">
                      <Portfolio size={48} className="mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-500">No portfolio items found</p>
                      <Button kind="tertiary" size="sm" className="mt-4" onClick={() => setCreateModalOpen(true)}>
                        Add your first item
                      </Button>
                    </div>
                  )}
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </Column>
      </Grid>

      {/* Create/Edit Modal */}
      <Modal
        open={createModalOpen}
        onRequestClose={() => {
          setCreateModalOpen(false);
          setEditingItem(null);
          resetForm();
        }}
        modalHeading={editingItem ? 'Edit Portfolio Item' : 'Add Portfolio Item'}
        primaryButtonText={editingItem ? 'Update' : 'Create'}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleCreateItem}
      >
        <div className="space-y-4">
          <TextInput id="title" labelText="Title" value={title} onChange={e => setTitle(e.target.value)} required />

          <TextArea
            id="description"
            labelText="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={4}
          />

          <Select id="category" labelText="Category" value={category} onChange={e => setCategory(e.target.value)}>
            <SelectItem value="project" text="Project" />
            <SelectItem value="certification" text="Certification" />
            <SelectItem value="achievement" text="Achievement" />
            <SelectItem value="article" text="Article" />
            <SelectItem value="presentation" text="Presentation" />
          </Select>

          <TextInput
            id="tags"
            labelText="Tags (comma-separated)"
            value={tags}
            onChange={e => setTags(e.target.value)}
            placeholder="e.g., react, typescript, design"
          />

          <Select
            id="visibility"
            labelText="Visibility"
            value={visibility}
            onChange={e => setVisibility(e.target.value as any)}
          >
            <SelectItem value="public" text="Public - Visible to everyone" />
            <SelectItem value="unlisted" text="Unlisted - Only with link" />
            <SelectItem value="private" text="Private - Only you" />
          </Select>

          <Toggle
            id="featured"
            labelText="Featured item"
            toggled={featured}
            onToggle={checked => setFeatured(checked)}
            labelA="No"
            labelB="Yes"
          />

          {!editingItem && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-2">Upload File</p>
              <FileUploader onUploadComplete={handleFileUpload} category="portfolio" defaultVisibility={visibility} />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PortfolioItems;
